import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { api, formatApiError } from "../lib/api";
import { BUILD_ITER as _RetiredBuildIter } from "../build"; // eslint-disable-line no-unused-vars
import { ShieldCheck as _RetiredShieldCheck } from "lucide-react"; // eslint-disable-line no-unused-vars
import OnSetFlowWordmark from "../components/OnSetFlowWordmark";
// ^ Iter-158.13.2.20f.3 #11 — ShieldCheck retired from AuthPage; the
// import is kept as an underscored alias so the tree-shake doesn't
// change until we decide the next-iteration polish (see PRD).

const AUTH_BG =
    "https://static.prod-images.emergentagent.com/jobs/1ab3e5cd-a620-45d1-bd08-7360f4789ce9/images/b48c5b5accfe2a8fbea415c336ee8cb8fcf9ebb399ef7faa616d676c39395998.png";

/**
 * Auth landing page. Hosts three modes:
 *  - "login":    sign in with email + password (default tab)
 *  - "register": create a new account
 *  - "forgot":   request a password-reset link
 *
 * The Forgot view is rendered in place of the tabs when the user clicks
 * the "Forgot password?" link inside the login tab. Returning to login is
 * a single tap. The page intentionally stays minimal — no tagline, just
 * the brand mark and the form.
 */
export default function AuthPage() {
    const navigate = useNavigate();
    const { login, register } = useAuth();
    const { theme } = useTheme();
    const [searchParams] = useSearchParams();
    // Iter-158.12.7 UAT fix — honour the `?next=` deep link query
    // param that JoinBetaProject.jsx (and any other auth-guarded
    // route) sets before bouncing an unauthenticated user through
    // /login. Without this the beta join flow drops the user at
    // Home instead of routing them through JoinBetaProject → beta
    // join endpoint, and the next click at the "Join a project"
    // affordance hits the LEGACY /projects/join form (which then
    // 404s because beta codes live in beta_projects). Only accept
    // in-app absolute paths ("/…") to prevent open-redirect abuse.
    const rawNext = searchParams.get("next") || "";
    const nextPath = rawNext.startsWith("/") && !rawNext.startsWith("//")
        ? rawNext
        : "/";
    const goNext = () => navigate(nextPath, { replace: true });
    const [tab, setTab] = useState("login");
    const [mode, setMode] = useState("auth"); // 'auth' | 'forgot'
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // login
    const [loginEmail, setLoginEmail] = useState("");
    const [loginPwd, setLoginPwd] = useState("");

    // register
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [pwd, setPwd] = useState("");

    // forgot
    const [forgotEmail, setForgotEmail] = useState("");
    const [forgotSent, setForgotSent] = useState(false);

    const handleLogin = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        const res = await login(loginEmail.trim(), loginPwd);
        setLoading(false);
        if (!res.ok) setError(res.error);
        else goNext();
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        const res = await register(name.trim(), email.trim(), pwd);
        setLoading(false);
        if (!res.ok) setError(res.error);
        else goNext();
    };

    const handleForgot = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await api.post("/auth/forgot-password", {
                email: forgotEmail.trim(),
            });
            // Backend always returns the same generic message — show it
            // verbatim so an attacker can't infer whether an account
            // exists by watching the UI.
            setForgotSent(true);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    const switchToForgot = () => {
        setError("");
        setForgotSent(false);
        // Pre-fill if the user already typed an email in the login tab.
        setForgotEmail(loginEmail);
        setMode("forgot");
    };

    const switchToLogin = () => {
        setError("");
        setMode("auth");
        setTab("login");
    };

    return (
        <div
            data-testid="auth-page"
            className="relative min-h-screen w-full flex flex-col"
            style={{
                // Iter-110 — keep the cinematic on-set photo backdrop in
                // dark mode, but lift the veil in light mode so the
                // canvas reads as bright + airy. The image is the same
                // — only the linear-gradient tint changes (a darkening
                // wash for dark mode, a soft white wash for light mode).
                backgroundImage:
                    theme === "light"
                        ? `linear-gradient(to bottom, rgba(245,245,245,0.85), rgba(245,245,245,0.95)), url(${AUTH_BG})`
                        : `linear-gradient(to bottom, rgba(0,0,0,0.55), rgba(0,0,0,0.85)), url(${AUTH_BG})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
            }}
        >
            <div className="flex-1 flex items-center justify-center px-5 py-10">
                <div className="w-full max-w-md">
                    <div className="mb-8">
                        {/* Iter-157N.2 — Login header restructured per
                            second pilot pass: shield removed from
                            the wordmark row, ownership line moved
                            BELOW the login box. The header now reads
                            simply: OnSetFlow · supporting tagline ·
                            login form · shield + ownership line ·
                            build marker. Cleaner visual flow with
                            the trust signal landing AFTER the
                            functional task. */}
                        {/* Iter-158.13.2.20f.42-kiwi2 — the canonical
                            OnSetFlow wordmark treatment lives in
                            <OnSetFlowWordmark/>. This site keeps the
                            hero login size (text-5xl / sm:text-6xl)
                            and the existing `login-wordmark` testid;
                            the rendered mark is byte-identical to
                            the previous inline h1 the product design
                            established. The masthead in ProjectHeader
                            reuses the same component at header size. */}
                        <OnSetFlowWordmark
                            as="h1"
                            testid="login-wordmark"
                            className="text-5xl sm:text-6xl"
                        />
                    </div>

                    <div className="rounded-xl border border-white/10 bg-card/80 backdrop-blur-md p-5">
                        {mode === "forgot" ? (
                            // ---------- Forgot-password panel ----------
                            <div data-testid="forgot-panel">
                                <h2 className="font-display text-xl font-bold text-white">
                                    Reset password
                                </h2>
                                <p className="text-xs text-neutral-400 mt-1.5 leading-relaxed">
                                    Enter the email tied to your account. We&rsquo;ll send a reset link if it matches a registered user.
                                </p>

                                {forgotSent ? (
                                    <div
                                        data-testid="forgot-sent"
                                        className="mt-5 rounded-lg border border-emerald-400/20 bg-emerald-400/5 p-4"
                                    >
                                        <p className="text-sm text-emerald-200">
                                            If an account exists for that email, we have sent reset instructions.
                                        </p>
                                        <p className="text-xs text-neutral-400 mt-2 leading-relaxed">
                                            Check your inbox &amp; spam. The link expires in 1&nbsp;hour.
                                        </p>
                                        <Button
                                            type="button"
                                            data-testid="forgot-back-login"
                                            variant="secondary"
                                            onClick={switchToLogin}
                                            className="w-full mt-4"
                                        >
                                            Back to sign in
                                        </Button>
                                    </div>
                                ) : (
                                    <form onSubmit={handleForgot} className="space-y-4 mt-5">
                                        <div className="space-y-2">
                                            <Label
                                                htmlFor="forgot-email"
                                                className="text-xs uppercase tracking-[0.2em] text-neutral-500"
                                            >
                                                Email
                                            </Label>
                                            <Input
                                                id="forgot-email"
                                                data-testid="forgot-email-input"
                                                type="email"
                                                autoComplete="email"
                                                value={forgotEmail}
                                                onChange={(e) => setForgotEmail(e.target.value)}
                                                required
                                                className="h-12 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                                            />
                                        </div>
                                        {error ? (
                                            <p
                                                data-testid="forgot-error"
                                                className="text-sm text-red-400"
                                            >
                                                {error}
                                            </p>
                                        ) : null}
                                        <Button
                                            data-testid="forgot-submit"
                                            type="submit"
                                            disabled={loading}
                                            className="w-full min-tap font-semibold tracking-wide"
                                        >
                                            {loading ? "Sending…" : "Send reset link"}
                                        </Button>
                                        <button
                                            type="button"
                                            data-testid="forgot-cancel"
                                            onClick={switchToLogin}
                                            className="tap w-full text-xs text-neutral-400 hover:text-white py-2"
                                        >
                                            Back to sign in
                                        </button>
                                    </form>
                                )}
                            </div>
                        ) : (
                            // ---------- Login + register tabs ----------
                            <Tabs value={tab} onValueChange={setTab}>
                                <TabsList
                                    data-testid="auth-tabs"
                                    className="w-full grid grid-cols-2 h-11 bg-secondary p-1"
                                >
                                    <TabsTrigger
                                        data-testid="tab-login"
                                        value="login"
                                        className="h-9 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                                    >
                                        Sign in
                                    </TabsTrigger>
                                    <TabsTrigger
                                        data-testid="tab-register"
                                        value="register"
                                        className="h-9 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                                    >
                                        Create account
                                    </TabsTrigger>
                                </TabsList>

                                <TabsContent value="login" className="mt-5">
                                    <form onSubmit={handleLogin} className="space-y-4">
                                        <div className="space-y-2">
                                            <Label
                                                htmlFor="login-email"
                                                className="text-xs uppercase tracking-[0.2em] text-neutral-500"
                                            >
                                                Email
                                            </Label>
                                            <Input
                                                id="login-email"
                                                data-testid="login-email-input"
                                                type="email"
                                                autoComplete="email"
                                                value={loginEmail}
                                                onChange={(e) => setLoginEmail(e.target.value)}
                                                required
                                                className="h-12 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <div className="flex items-center justify-between">
                                                <Label
                                                    htmlFor="login-password"
                                                    className="text-xs uppercase tracking-[0.2em] text-neutral-500"
                                                >
                                                    Password
                                                </Label>
                                                {/* Forgot-password link sits
                                                    inline with the password
                                                    label so it's visible
                                                    without scrolling and
                                                    impossible to miss. */}
                                                <button
                                                    type="button"
                                                    data-testid="forgot-password-link"
                                                    onClick={switchToForgot}
                                                    className="tap text-[11px] text-neutral-400 hover:text-white"
                                                >
                                                    Forgot password?
                                                </button>
                                            </div>
                                            <Input
                                                id="login-password"
                                                data-testid="login-password-input"
                                                type="password"
                                                autoComplete="current-password"
                                                value={loginPwd}
                                                onChange={(e) => setLoginPwd(e.target.value)}
                                                required
                                                className="h-12 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                                            />
                                        </div>
                                        {error && tab === "login" ? (
                                            <p
                                                data-testid="auth-error"
                                                className="text-sm text-red-400"
                                            >
                                                {error}
                                            </p>
                                        ) : null}
                                        <Button
                                            data-testid="login-submit"
                                            type="submit"
                                            disabled={loading}
                                            className="w-full min-tap font-semibold tracking-wide"
                                        >
                                            {loading ? "Signing in…" : "Sign in"}
                                        </Button>
                                    </form>
                                </TabsContent>

                                <TabsContent value="register" className="mt-5">
                                    <form onSubmit={handleRegister} className="space-y-4">
                                        <div className="space-y-2">
                                            <Label
                                                htmlFor="reg-name"
                                                className="text-xs uppercase tracking-[0.2em] text-neutral-500"
                                            >
                                                Your name
                                            </Label>
                                            <Input
                                                id="reg-name"
                                                data-testid="register-name-input"
                                                value={name}
                                                onChange={(e) => setName(e.target.value)}
                                                required
                                                className="h-12 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label
                                                htmlFor="reg-email"
                                                className="text-xs uppercase tracking-[0.2em] text-neutral-500"
                                            >
                                                Email
                                            </Label>
                                            <Input
                                                id="reg-email"
                                                data-testid="register-email-input"
                                                type="email"
                                                autoComplete="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                required
                                                className="h-12 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label
                                                htmlFor="reg-pwd"
                                                className="text-xs uppercase tracking-[0.2em] text-neutral-500"
                                            >
                                                Password{" "}
                                                <span className="normal-case tracking-normal text-neutral-600">
                                                    (min 6)
                                                </span>
                                            </Label>
                                            <Input
                                                id="reg-pwd"
                                                data-testid="register-password-input"
                                                type="password"
                                                autoComplete="new-password"
                                                value={pwd}
                                                onChange={(e) => setPwd(e.target.value)}
                                                required
                                                minLength={6}
                                                className="h-12 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                                            />
                                        </div>
                                        {error && tab === "register" ? (
                                            <p
                                                data-testid="auth-error"
                                                className="text-sm text-red-400"
                                            >
                                                {error}
                                            </p>
                                        ) : null}
                                        <Button
                                            data-testid="register-submit"
                                            type="submit"
                                            disabled={loading}
                                            className="w-full min-tap font-semibold tracking-wide"
                                        >
                                            {loading ? "Creating…" : "Create account"}
                                        </Button>
                                    </form>
                                </TabsContent>
                            </Tabs>
                        )}
                    </div>

                    {/* Iter-158.13.2.20f.3 #11 — retired the shield +
                        "Create continuity / Keep it" ownership block
                        and the stale BUILD_ITER identifier (was
                        pinned at iter-158.13.2.16 for months and
                        misled human UAT). Replaced with an
                        onsetflow.com footer link so external testers
                        know where the marketing surface lives. */}
                    <p
                        data-testid="login-footer-link"
                        className="text-[12px] text-neutral-400 mt-6 text-center"
                    >
                        <a
                            href="https://onsetflow.com"
                            target="_blank"
                            rel="noreferrer noopener"
                            className="hover:text-neutral-200"
                        >
                            onsetflow.com
                        </a>
                    </p>
                </div>
            </div>
        </div>
    );
}
